import { useState, useEffect, type ChangeEvent } from 'react';
import StatusMessage, { type FetchState } from '../components/shared/StatusMessage';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { useBatch } from '../context/BatchContext';
import { AUDIT_ACTION_OPTIONS, PHOTO_SCOPED_ACTIONS } from '../constants/auditActions';
import type { AuditLog as AuditLogEntry, Batch, User } from '../types';

const PAGE_SIZE = 50;

const fmtTime = (iso: string) => new Date(iso).toLocaleString();
const fmtUser = (u: AuditLogEntry['userId']) => u ? `${u.fName} ${u.lName}` : 'system';
const fmtPhoto = (p: AuditLogEntry['photoId'], actionType: string) => {
    if (p) return p.originalFilename;
    // No photoId: only photo-scoped actions imply a since-deleted photo;
    // batch-level / non-photo actions have no photo at all.
    if (PHOTO_SCOPED_ACTIONS.has(actionType)) return '(deleted photo)';
    return '—';
};
const fmtBatch = (batchId: number | null, batches: Batch[]) => {
    if (!batchId) return '—';
    const batch = batches.find(b => b.id === batchId);
    return batch ? batch.galleryName : '—';
};

export default function AuditLog() {
    const { batches, batchId, setBatchId } = useBatch();
    const [logs, setLogs] = useState<AuditLogEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [state, setState] = useState<FetchState>({ status: 'loading', error: null });
    const [refreshToken, setRefreshToken] = useState(0);

    // Additional additive filters (combine freely with the batch filter).
    const [users, setUsers] = useState<User[]>([]);
    const [userId, setUserId] = useState('');
    const [actionType, setActionType] = useState('');
    const [photoInput, setPhotoInput] = useState('');   // bound to the text input
    const [photoFilter, setPhotoFilter] = useState(''); // debounced value used for fetching

    // Export state
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState('');

    const offset = (page - 1) * PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const firstEntry = total === 0 ? 0 : offset + 1;
    const lastEntry = Math.min(offset + PAGE_SIZE, total);

    // Populate the acting-user dropdown once on mount (all roles can appear).
    useEffect(() => {
        apiFetch('/api/users', { headers: authHeaders() })
            .then(r => r.json())
            .then((data: unknown) => setUsers(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);

    // Debounce the photo-filename search so we don't fire a request per keystroke.
    useEffect(() => {
        const handle = setTimeout(() => {
            const next = photoInput.trim();
            setPhotoFilter(prev => {
                if (prev !== next) setPage(1);
                return next;
            });
        }, 350);
        return () => clearTimeout(handle);
    }, [photoInput]);

    useEffect(() => {
        // Same "set loading, then fetch" shape used throughout this codebase
        // (see UserManagement, ArtistPortfolio, Photos); the experimental
        // react-hooks/set-state-in-effect rule only flags it here.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setState({ status: 'loading', error: null });
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
        if (batchId) params.set('batchId', batchId);
        if (userId) params.set('userId', userId);
        if (actionType) params.set('actionType', actionType);
        if (photoFilter) params.set('photoFilename', photoFilter);

        apiFetch(`/api/audit?${params}`, { headers: authHeaders() })
            .then(r => r.json())
            .then((body: { logs: AuditLogEntry[]; total: number }) => {
                setLogs(body.logs || []);
                setTotal(body.total ?? 0);
                setState({ status: 'ready', error: null });
            })
            .catch(err => setState({ status: 'error', error: err instanceof Error ? err.message : String(err) }));
    }, [batchId, userId, actionType, photoFilter, offset, refreshToken]);

    const retry = () => setRefreshToken(n => n + 1);

    const handleBatchChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setPage(1);
        setBatchId(e.target.value);
    };

    const handleUserChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setPage(1);
        setUserId(e.target.value);
    };

    const handleActionChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setPage(1);
        setActionType(e.target.value);
    };

    const handleExport = async () => {
        if (!batchId) return;
        setExportError('');
        setExporting(true);
        try {
            const params = new URLSearchParams({ batchId });
            if (userId) params.set('userId', userId);
            if (actionType) params.set('actionType', actionType);
            if (photoFilter) params.set('photoFilename', photoFilter);

            const res = await apiFetch(`/api/audit/export?${params}`, { headers: authHeaders() });
            if (!res.ok) {
                setExportError(`Export failed (${res.status}).`);
                return;
            }
            const blob = await res.blob();
            const objectURL = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectURL;
            const disposition = res.headers.get('Content-Disposition') ?? '';
            const match = disposition.match(/filename="([^"]+)"/);
            a.download = match ? match[1] : 'audit.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectURL);
        } catch (err) {
            setExportError(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className='audit-log'>
            <div className='audit-log-controls'>
                <label className='audit-filter-label'>
                    Filter by batch
                    <select
                        className='audit-filter-select'
                        value={batchId}
                        onChange={handleBatchChange}
                    >
                        <option value=''>All batches</option>
                        {batches.map(b => (
                            <option key={b.id} value={b.id}>{b.galleryName}</option>
                        ))}
                    </select>
                </label>

                <label className='audit-filter-label'>
                    Filter by user
                    <select
                        className='audit-filter-select'
                        value={userId}
                        onChange={handleUserChange}
                    >
                        <option value=''>All users</option>
                        {users.map(u => (
                            <option key={u.id} value={u.id}>
                                {u.fName} {u.lName} ({u.role})
                            </option>
                        ))}
                    </select>
                </label>

                <label className='audit-filter-label'>
                    Filter by action
                    <select
                        className='audit-filter-select'
                        value={actionType}
                        onChange={handleActionChange}
                    >
                        <option value=''>All actions</option>
                        {AUDIT_ACTION_OPTIONS.map(a => (
                            <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                    </select>
                </label>

                <label className='audit-filter-label'>
                    Photo
                    <input
                        type='text'
                        className='audit-filter-select audit-filter-input'
                        placeholder='Photo filename…'
                        value={photoInput}
                        onChange={e => setPhotoInput(e.target.value)}
                    />
                </label>

                <button
                    className='audit-page-btn audit-export-btn'
                    onClick={handleExport}
                    disabled={!batchId || exporting}
                    title={batchId ? 'Export filtered audit log as CSV' : 'Select a batch to export'}
                >
                    {exporting ? 'Exporting…' : 'Export CSV'}
                </button>
            </div>

            {exportError && (
                <p className='audit-export-error' role='alert'>{exportError}</p>
            )}

            {state.status !== 'ready' && (
                <StatusMessage state={state} onRetry={retry} />
            )}

            {state.status === 'ready' && logs.length === 0 && (
                <p>No audit logs match the current filters.</p>
            )}

            {state.status === 'ready' && logs.length > 0 && (
                <>
                <div className='table-responsive'>
                <table className='audit-table'>
                    <thead>
                        <tr>
                            <th>Timestamp</th>
                            <th>Action</th>
                            <th>Batch</th>
                            <th>Photo</th>
                            <th>User</th>
                            <th>IP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr key={log.id}>
                                <td>{fmtTime(log.timestamp)}</td>
                                <td><code>{log.actionType}</code></td>
                                <td>{fmtBatch(log.batchId, batches)}</td>
                                <td>{fmtPhoto(log.photoId, log.actionType)}</td>
                                <td>{fmtUser(log.userId)}</td>
                                <td>{log.ipAddress || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                <div className='audit-pagination'>
                    <button
                        className='audit-page-btn'
                        onClick={() => setPage(p => p - 1)}
                        disabled={page <= 1}
                    >
                        ← Previous
                    </button>
                    <span className='audit-page-info'>
                        {firstEntry}–{lastEntry} of {total} &middot; page {page} of {totalPages}
                    </span>
                    <button
                        className='audit-page-btn'
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= totalPages}
                    >
                        Next →
                    </button>
                </div>
                </>
            )}
        </div>
    );
}
